import { memo, useCallback, useDeferredValue, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tree } from 'react-arborist';
import type { NodeRendererProps } from 'react-arborist';
import { Building2, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore, useRootLabel, findNodeInTree, type NodeTreeItem, type NodesResponse } from '@/stores/scope';
import { NodeTypeIconChip } from '@/components/NodeTypeIcon';

type NodeSwitcherRowProps = {
  node: NodeRendererProps<NodeTreeItem>['node'];
  style: NodeRendererProps<NodeTreeItem>['style'];
  isSelected: boolean;
  onPick: (node: NodeTreeItem) => void;
};

/** モジュールスコープで1度だけ定義し React.memo でラップする。NodeSwitcher は AppLayout 経由で
 *  ほぼ全ページのヘッダーに常時マウントされ、組織ツリー全量を扱うため、行コンポーネントを
 *  NodeSwitcher 内部で定義したまま (再レンダーのたびに別コンポーネント扱いになり react-arborist が
 *  可視行を毎回アンマウント/リマウントする) にしておくと影響範囲が最も広い (2026-08-11、
 *  StoreMultiSelect の同型不具合の横展開修正)。 */
const NodeSwitcherRow = memo(function NodeSwitcherRow({ node, style, isSelected, onPick }: NodeSwitcherRowProps) {
  return (
    <div
      style={style}
      role="button"
      tabIndex={-1}
      onClick={() => onPick(node.data)}
      className={`flex items-center gap-1 px-1 rounded cursor-pointer hover:bg-neutral-panel ${
        isSelected ? 'bg-neutral-panel font-semibold' : ''
      }`}
    >
      {node.isInternal ? (
        // tabIndex=-1: react-arborist の roving tabindex (行=単一 Tab stop) を壊さないよう、
        // ネストした button 自体は Tab 対象から外す (StoreMultiSelect と同一パターン)
        <button
          type="button"
          tabIndex={-1}
          onClick={(e) => { e.stopPropagation(); node.toggle(); }}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] text-neutral-sub shrink-0 sm:min-w-0 sm:min-h-0 sm:p-0.5"
          aria-label={node.isOpen ? '折り畳む' : '開く'}
        >
          {node.isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
      ) : (
        <span className="w-[44px] shrink-0 sm:w-[18px]" />
      )}
      <NodeTypeIconChip icon={node.data.icon} depth={node.level} />
      <span className="truncate text-sm">{node.data.name}</span>
      <span className="text-[11px] text-neutral-sub ml-auto shrink-0 pl-2">{node.data.label}</span>
    </div>
  );
});

/** AppLayout ヘッダーに埋め込むスコープ切替 UI (v0.2.5 S-9、docs/design/v025-config-ui.md §2-3)。
 *  「今見ているスコープ」を全ページで共有する単一ノード選択 — 比較対象の複数店舗選択
 *  (StoreMultiSelect) とは役割が異なる (§1.1 参照)。
 *  react-arborist 採用理由: Radix DropdownMenu (role="menu") と ARIA tree の role 混在を避けるため
 *  (§0 UI-C2 対応)。パネル自体は独自の軽量トグル実装 (AppLayout のモバイルドロワーと同じ
 *  「開閉 state + backdrop クリック + Escape」パターンを踏襲、新規に Radix Popover を追加しない)。 */
export function NodeSwitcher() {
  const tenantId = useAuthStore((s) => s.user?.tenant_id ?? null);
  const roots = useScopeStore((s) => s.roots);
  const hierarchyManagementEnabled = useScopeStore((s) => s.hierarchyManagementEnabled);
  const selectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const recentNodeIds = useScopeStore((s) => s.recentNodeIds);
  const setTree = useScopeStore((s) => s.setTree);
  const setSelectedNodeId = useScopeStore((s) => s.setSelectedNodeId);
  const rootLabel = useRootLabel();

  const { data } = useQuery<NodesResponse>({
    queryKey: ['nodes'],
    queryFn: () => apiFetch<NodesResponse>('/nodes'),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (data && tenantId) setTree(tenantId, data);
  }, [data, tenantId, setTree]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // モバイル (sm 未満) では tree 行の tap ターゲットを 44×44 確保するため行高を切り替える
  // (docs/design/v025-config-ui.md §5、StoreMultiSelect と同一パターン)
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches,
  );
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    // Tab キーをパネル内の先頭/末尾要素で折り返し、背後のページへフォーカスが漏れないようにする
    // (フォーカストラップ、AppLayout のモバイルドロワーには無いが role="dialog" のため必須)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), input, [tabindex]:not([tabindex="-1"])'),
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      // 閉じた際は開いたトリガーボタンへフォーカスを戻す (AppLayout モバイルドロワーと同じパターン)
      triggerRef.current?.focus();
    };
  }, [open]);

  // Hooks は早期 return より前で全て呼び切る必要があるため、行コンポーネントへ渡す
  // コールバックもここで確定させる (react-arborist の children には安定した参照を渡す)
  const handlePick = useCallback((node: NodeTreeItem | null) => {
    if (!tenantId) return;
    setSelectedNodeId(tenantId, node?.node_id ?? null);
    setOpen(false);
  }, [tenantId, setSelectedNodeId]);

  const renderRow = useCallback(
    ({ node, style }: NodeRendererProps<NodeTreeItem>) => (
      <NodeSwitcherRow node={node} style={style} isSelected={node.data.node_id === selectedNodeId} onPick={handlePick} />
    ),
    [selectedNodeId, handlePick],
  );

  // 検索1文字ごとの再走査で入力がカクつかないよう、絞り込み計算は低優先度に遅延させる
  // (React 18 useDeferredValue、StoreMultiSelect と同一パターン、2026-08-11)
  const deferredQuery = useDeferredValue(query);

  // tenant_features.hierarchy_management OFF のテナントでは NodeSwitcher 自体を非表示
  // (docs/design/v025-config-ui.md §0.5 E-3)。tree 未取得 (roots 空) の間も一旦非表示。
  if (!hierarchyManagementEnabled || roots.length === 0) return null;

  const selectedNode = selectedNodeId ? findNodeInTree(roots, selectedNodeId) : null;
  const recentNodes = recentNodeIds
    .map((id) => findNodeInTree(roots, id))
    .filter((n): n is NodeTreeItem => n !== null && n.node_id !== selectedNodeId);

  const pathTo = (nodeId: string): NodeTreeItem[] => {
    const dfs = (nodes: NodeTreeItem[], trail: NodeTreeItem[]): NodeTreeItem[] | null => {
      for (const n of nodes) {
        const nextTrail = [...trail, n];
        if (n.node_id === nodeId) return nextTrail;
        const hit = dfs(n.children, nextTrail);
        if (hit) return hit;
      }
      return null;
    };
    return dfs(roots, []) ?? [];
  };
  const breadcrumbPath = selectedNode ? pathTo(selectedNode.node_id) : [];
  const lastLabel = breadcrumbPath.length > 0 ? breadcrumbPath[breadcrumbPath.length - 1]!.name : rootLabel;

  const normalize = (s: string) => s.toLowerCase().replace(/[\s　]+/g, '');

  return (
    // ⚠️ min-w-0 が無いと、ヘッダー中央ブロック(justify-center)の flex item として
    //    「縮まない箱」になり、店舗名が長いと左右へはみ出してロゴやログアウトに重なる
    //    (2026-09-10 実測: 320pxでロゴに42px重なり、§8-189)。w-full は狭幅で
    //    ボタン自身を親の幅まで縮ませ、下の truncate を実際に効かせるために要る
    //    (button は width:auto だと内容幅のままで縮まないため)。
    <div className="relative min-w-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded text-sm text-chrome-text-sub hover:bg-chrome-raised hover:text-white transition min-w-0 w-full sm:w-auto max-w-[46vw] sm:max-w-xs"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="スコープ切替"
      >
        <Building2 size={16} className="shrink-0" />
        <span className="truncate hidden sm:inline">
          {rootLabel}{breadcrumbPath.map((n) => ` › ${n.name}`).join('')}
        </span>
        <span className="truncate sm:hidden">{lastLabel}</span>
        <ChevronDown size={14} className="shrink-0" />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="スコープ切替パネル"
          // モバイル (375px 想定) では画面下部からスライドインする bottom sheet として表示し、
          // sm 以上では従来通りトリガー直下の dropdown として表示する (docs/design/v025-config-ui.md §5、
          // StoreMultiSelect と同一パターン)。backdrop クリック / Escape / フォーカストラップは
          // 既存の useEffect をそのまま流用する。
          className="fixed inset-x-0 bottom-0 z-[45] max-h-[70vh] w-full overflow-y-auto rounded-t bg-white border-t border-neutral-line shadow-lg sm:absolute sm:inset-x-auto sm:bottom-auto sm:left-0 sm:top-full sm:mt-1 sm:w-80 sm:max-w-[90vw] sm:max-h-none sm:overflow-hidden sm:rounded sm:border"
        >
          <div className="p-2 border-b border-neutral-line">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-sub" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="ノードを検索"
                className="w-full pl-7 pr-2 py-1.5 text-sm border border-neutral-line rounded focus-ring"
                aria-label="ノード検索"
                autoFocus
              />
            </div>
          </div>
          {recentNodes.length > 0 && (
            <div className="px-3 py-2 border-b border-neutral-line">
              <div className="text-[11px] text-neutral-sub mb-1">最近使用</div>
              <div className="flex flex-wrap gap-1">
                {recentNodes.map((n) => (
                  <button
                    key={n.node_id}
                    type="button"
                    onClick={() => handlePick(n)}
                    className="px-2 py-0.5 rounded-full border border-neutral-line text-xs text-neutral-text hover:bg-neutral-panel"
                  >
                    {n.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="py-1">
            <button
              type="button"
              onClick={() => handlePick(null)}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-neutral-panel ${
                selectedNodeId === null ? 'font-semibold text-brand-red' : 'text-neutral-text'
              }`}
            >
              {rootLabel}（すべて）
            </button>
            <div className="px-1">
              <Tree<NodeTreeItem>
                data={roots}
                idAccessor="node_id"
                childrenAccessor="children"
                openByDefault={false}
                searchTerm={deferredQuery}
                searchMatch={(node, term) =>
                  normalize(node.data.name).includes(normalize(term)) || normalize(node.data.node_code).includes(normalize(term))
                }
                width="100%"
                height={280}
                indent={16}
                rowHeight={isMobile ? 44 : 30}
                // キーボードのみでのノード選択 (Space、葉ノードのみ react-arborist の既定挙動) に対応
                // (WCAG 2.1.1 対応。マウスクリックは NodeSwitcherRow 自身の onClick で既に処理済み)
                onActivate={(node) => handlePick(node.data)}
              >
                {renderRow}
              </Tree>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
